import axios, { type AxiosInstance, type AxiosResponse } from 'axios';

/**
 * EFS API client.
 *
 * Conventions (see `docs/specs/06-ui-api-spec.md` §4 and `02-architecture` §7):
 *  - Envelope: `{ data, meta, warnings }` for success.
 *  - Error envelope: `{ error: { code, message, details } }`.
 *  - kg values are strings — we never coerce to number here.
 *  - Locale injected via `Accept-Language` header so the backend can return
 *    localized exception messages.
 *  - 401 is returned to the caller AND a registered global handler fires so a
 *    session that expires mid-session bounces the user to /login. Route guards
 *    only run on mount, so without this an expired session shows empty pages.
 */

export interface ApiSuccess<TData, TMeta = Record<string, unknown>> {
  data: TData;
  meta?: TMeta;
  warnings?: ApiWarning[];
}

export interface ApiWarning {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  /**
   * Correlation id: `error.details.request_id` from the body when present,
   * else the `x-request-id` response header the backend always sets
   * (spec-13 §10 — CORS exposes the header for cross-origin deployments).
   */
  readonly requestId?: string;

  constructor(status: number, error: ApiError, headerRequestId?: string) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
    // The server-generated response header is authoritative. Never let an
    // error-body detail override support correlation with a spoofed value.
    this.requestId = isUuid(headerRequestId) ? headerRequestId : undefined;
  }
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** Read the correlation id from an axios-style response headers bag. */
export function requestIdFromHeaders(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const bag = headers as Record<string, unknown>;
  const raw = bag['x-request-id'] ?? bag['X-Request-Id'];
  return isUuid(raw) ? raw : undefined;
}

/**
 * Global 401 handler registry. The axios interceptor cannot import the router
 * (would create an import cycle) so callers register a handler instead. Fired
 * on every 401 except the `/me` bootstrap probe (which legitimately 401s while
 * unauthenticated and is handled by the route guard). Registered in
 * `src/app/providers.tsx`.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Inject current i18n locale on every request.
apiClient.interceptors.request.use((config) => {
  const lang = typeof window !== 'undefined' ? window.localStorage.getItem('efs.lang') : null;
  if (lang) {
    config.headers = config.headers ?? {};
    config.headers['Accept-Language'] = lang;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Network / no-response errors: surface as ApiClientError(0, ...)
    if (!error.response) {
      return Promise.reject(
        new ApiClientError(0, {
          code: 'NETWORK_ERROR',
          message: error.message ?? 'Network error',
        }),
      );
    }

    const status = error.response.status ?? 0;
    const body = error.response.data;
    const headerRequestId = requestIdFromHeaders(error.response.headers);
    const apiErr: ApiError =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: ApiError }).error
        : { code: 'UNKNOWN_ERROR', message: error.message ?? 'Unknown error' };

    // Session expired mid-flight → notify the global handler so the app can
    // redirect to /login. The `/me` probe opts out (its URL ends in `/me`) so
    // the unauthenticated bootstrap doesn't trigger a redirect loop.
    if (status === 401 && onUnauthorized) {
      const url = error.config?.url ?? '';
      if (!url.endsWith('/me')) onUnauthorized();
    }

    return Promise.reject(new ApiClientError(status, apiErr, headerRequestId));
  },
);

/** Unwrap `{ data, meta, warnings }` and return just `data`. */
export async function unwrap<TData>(
  promise: Promise<AxiosResponse<ApiSuccess<TData>>>,
): Promise<TData> {
  const res = await promise;
  return res.data.data;
}
